import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { ShoppingCart, X, Plus, Minus, ShoppingBag, Truck, SquaresFour, ListBullets, MagnifyingGlass } from "@phosphor-icons/react";
import { HighlightBadge } from "@/components/HighlightBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_KEY = "provertical-cart";
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

function loadCartFromStorage() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get("cat") || "all");
  const [viewMode, setViewMode] = useState(typeof window !== "undefined" && localStorage.getItem("provertical-store-view") === "list" ? "list" : "grid");

  // Sync URL ?cat= with category state
  useEffect(() => {
    const urlCat = searchParams.get("cat") || "all";
    if (urlCat !== category) setCategory(urlCat);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCategory = (v) => {
    setCategory(v);
    if (v === "all") setSearchParams({});
    else setSearchParams({ cat: v });
  };
  const [cart, setCart] = useState(loadCartFromStorage());
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [delivery, setDelivery] = useState({ method: "shipping", address: "", city: "", region: "", notes: "", event_id: "", event_name: "" });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickBuyProduct, setQuickBuyProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountInfo, setDiscountInfo] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
    axios.get(`${API}/events`).then(r => setEvents(r.data)).catch(() => {});
  }, []);

  // Persist cart to localStorage whenever it changes (shared with ProductDetail.js)
  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
  }, [cart]);

  // Open cart dialog when arriving with ?openCart=1 (from PDP "Comprar ahora")
  useEffect(() => {
    if (searchParams.get("openCart") === "1") {
      // Re-read from storage in case PDP just wrote there
      const fresh = loadCartFromStorage();
      setCart(fresh);
      setCartOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("openCart");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync cart across tabs / when PDP writes while Store mounted
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CART_KEY) {
        try { setCart(e.newValue ? JSON.parse(e.newValue) : []); } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const CATEGORIES = [
    { value: "all", label: t("cat_all") }, { value: "zapatillas", label: t("cat_shoes") },
    { value: "ropa", label: t("cat_clothing") }, { value: "equipamiento", label: t("cat_equipment") },
    { value: "accesorios", label: t("cat_accessories") },
  ];

  const byCategory = category === "all" ? products : products.filter(p => p.category === category);
  const q = searchQuery.trim().toLowerCase();
  const filtered = !q ? byCategory : byCategory.filter(p =>
    (p.name || "").toLowerCase().includes(q) ||
    (p.description || "").toLowerCase().includes(q) ||
    (p.category || "").toLowerCase().includes(q)
  );

  const addToCart = (product) => {
    setCart(prev => { const ex = prev.find(i => i.id === product.id); if (ex) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i); return [...prev, { ...product, quantity: 1 }]; });
    toast.success(`${product.name} agregado al carrito`);
  };
  const updateQuantity = (id, d) => setCart(prev => prev.map(i => { if (i.id !== id) return i; const nq = i.quantity + d; return nq < 1 ? i : { ...i, quantity: nq }; }));
  const removeFromCart = (id) => setCart(prev => prev.filter(i => i.id !== id));
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const finalTotal = discountInfo ? discountInfo.final_price : cartTotal;

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountLoading(true);
    try {
      const { data } = await axios.post(`${API}/discount-codes/validate`, { code: discountCode, item_type: "product", item_id: "", original_price: cartTotal });
      setDiscountInfo(data); toast.success(`Descuento aplicado: ${data.description}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Codigo invalido"); setDiscountInfo(null); }
    finally { setDiscountLoading(false); }
  };

  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) { toast.error(t("fill_all_fields")); return; }
    if (delivery.method === "shipping" && (!delivery.address || !delivery.city)) { toast.error(lang === "en" ? "Complete shipping address" : "Completa la direccion de envio"); return; }
    if (delivery.method === "event_pickup" && !delivery.event_id) { toast.error(lang === "en" ? "Select an event" : "Selecciona un evento"); return; }
    if (delivery.method === "coordinate" && !delivery.address.trim()) { toast.error(lang === "en" ? "Indicate preferred pickup location" : "Indica un lugar preferido"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        items: cart.map(i => ({ product_id: i.id, quantity: i.quantity, selected_size: i.selected_size || "", selected_color: i.selected_color || "" })),
        customer_name: customerInfo.name, customer_email: customerInfo.email, customer_phone: customerInfo.phone,
        delivery_method: delivery.method, pickup_event_id: delivery.event_id, pickup_event_name: delivery.event_name,
        shipping_address: delivery.address, shipping_city: delivery.city, shipping_region: delivery.region, shipping_notes: delivery.notes,
        discount_code: discountCode
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; }
      else {
        toast.success(`${t("order_created")} ${data.order_id.slice(0, 8)}`);
        if (data.whatsapp_url) { window.open(data.whatsapp_url, "_blank"); }
        setCart([]); setCheckoutOpen(false); setCartOpen(false);
      }
    } catch (err) { toast.error(err.response?.data?.detail || t("error_generic")); } finally { setLoading(false); }
  };

  const { lang } = useTranslation();

  return (
    <main className="pt-20 min-h-screen">
      <section className="py-16 sm:py-20" data-testid="store-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">{t("store_label")}</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk']">{t("store_title")}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="inline-flex border border-white/10 rounded overflow-hidden" data-testid="store-view-toggle">
                <button type="button" aria-label="Vista cuadricula" onClick={() => { setViewMode("grid"); localStorage.setItem("provertical-store-view", "grid"); }}
                  className={`px-3 h-10 flex items-center justify-center transition-colors ${viewMode === "grid" ? "bg-[#FF4D00] text-white" : "bg-[#1A1A1A] text-gray-400 hover:text-white"}`} data-testid="store-view-grid"><SquaresFour size={18} weight="bold" /></button>
                <button type="button" aria-label="Vista lista" onClick={() => { setViewMode("list"); localStorage.setItem("provertical-store-view", "list"); }}
                  className={`px-3 h-10 flex items-center justify-center transition-colors ${viewMode === "list" ? "bg-[#FF4D00] text-white" : "bg-[#1A1A1A] text-gray-400 hover:text-white"}`} data-testid="store-view-list"><ListBullets size={18} weight="bold" /></button>
              </div>
              <Select value={category} onValueChange={updateCategory}>
                <SelectTrigger className="w-44 bg-[#1A1A1A] border-white/10" data-testid="store-category-filter"><SelectValue placeholder={t("category")} /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" className="border-white/10 relative" onClick={() => setCartOpen(true)} data-testid="store-cart-button">
                <ShoppingCart size={18} />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#FF4D00] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{cartCount}</span>}
              </Button>
            </div>
          </div>
        </div>
      </section>
      <section className="pb-24" data-testid="store-products">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Buscador */}
          <div className="mb-6">
            <div className="relative max-w-xl">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t("store_search_placeholder") || "Buscar productos..."}
                className="w-full bg-[#1A1A1A] border border-white/10 focus:border-[#FF4D00]/50 focus:outline-none pl-10 pr-10 py-2.5 text-sm text-white placeholder:text-gray-500 transition-colors"
                data-testid="store-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  aria-label="Limpiar busqueda"
                  data-testid="store-search-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-gray-500 mt-2" data-testid="store-search-results-count">
                {filtered.length === 0
                  ? "Sin resultados para esta busqueda"
                  : `${filtered.length} ${filtered.length === 1 ? "resultado" : "resultados"} para "${searchQuery}"`}
              </p>
            )}
          </div>
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
            className={viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6" : "flex flex-col gap-4"}>
            {filtered.map(product => viewMode === "grid" ? (
              <motion.div key={product.id} variants={fadeUp} className="bg-[#121212] border border-white/5 overflow-hidden card-hover group flex flex-col" data-testid={`product-card-${product.id}`}>
                <Link to={`/tienda/producto/${product.id}`} className="block">
                  <div className="product-img-wrap h-36 sm:h-56 bg-[#1A1A1A] relative">
                    <img src={product.image_url || (product.images || [])[0]} alt={product.name} className="w-full h-full object-cover" />
                    <HighlightBadge text={product.badge_text} color={product.badge_color} />
                    {(product.images || []).length > 0 && (
                      <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">+{(product.images || []).length}</span>
                    )}
                  </div>
                </Link>
                <div className="p-3 sm:p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2 flex-wrap">
                    <Badge variant="outline" className="text-gray-400 border-white/10 text-[10px] sm:text-xs px-1.5 sm:px-2">{product.category}</Badge>
                    {product.gender && <Badge variant="outline" className="hidden sm:inline-flex text-blue-300 border-blue-300/20 text-xs capitalize">{product.gender}</Badge>}
                    {product.featured && <Badge className="bg-[#FF4D00]/20 text-[#FF4D00] border-[#FF4D00]/30 text-[10px] sm:text-xs px-1.5 sm:px-2">{t("featured")}</Badge>}
                    {product.is_dropshipping && product.shipping_days && (
                      <Badge variant="outline" className="hidden sm:inline-flex text-blue-400 border-blue-400/30 text-xs items-center gap-1"><Truck size={10} weight="bold" /> {product.shipping_days}</Badge>
                    )}
                  </div>
                  <Link to={`/tienda/producto/${product.id}`} className="hover:text-[#FF4D00] transition-colors">
                    <h3 className="text-sm sm:text-base font-semibold mb-1 font-['Cabinet_Grotesk'] line-clamp-2">{product.name}</h3>
                  </Link>
                  <p className="text-[11px] sm:text-xs text-gray-400 mb-3 sm:mb-4 line-clamp-2 hidden sm:block whitespace-pre-wrap">{product.description}</p>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-auto">
                    <span className="text-base sm:text-lg font-bold text-[#FF4D00]">{formatPrice(product.price)}</span>
                    <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                      <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:text-white text-[11px] sm:text-xs h-8 sm:h-9 px-2 sm:px-3 flex-1 sm:flex-none" onClick={() => addToCart(product)} disabled={product.stock <= 0} data-testid={`add-to-cart-${product.id}`}>
                        {product.stock <= 0 ? t("sold_out") : t("add_to_cart")}
                      </Button>
                      {product.stock > 0 && (
                        <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold text-[11px] sm:text-xs h-8 sm:h-9 px-2 sm:px-3 flex-1 sm:flex-none" data-testid={`quick-buy-${product.id}`}
                          onClick={() => { setQuickBuyProduct(product); setCart([{ ...product, quantity: 1 }]); setCheckoutOpen(true); }}>
                          {lang === "en" ? "Buy" : "Comprar"}
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-2">{product.stock > 0 ? (lang === "en" ? "Available" : "Disponible") : t("no_stock")}</p>
                </div>
              </motion.div>
            ) : (
              <motion.div key={product.id} variants={fadeUp} className="bg-[#121212] border border-white/5 overflow-hidden flex flex-col sm:flex-row gap-0 hover:border-white/15 transition-colors" data-testid={`product-card-${product.id}`}>
                <Link to={`/tienda/producto/${product.id}`} className="sm:w-48 flex-shrink-0">
                  <div className="h-48 sm:h-full bg-[#1A1A1A] relative">
                    <img src={product.image_url || (product.images || [])[0]} alt={product.name} className="w-full h-full object-cover" />
                    <HighlightBadge text={product.badge_text} color={product.badge_color} />
                  </div>
                </Link>
                <div className="p-5 flex-1 flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-gray-400 border-white/10 text-xs">{product.category}</Badge>
                    {product.gender && <Badge variant="outline" className="text-blue-300 border-blue-300/20 text-xs capitalize">{product.gender}</Badge>}
                    {product.featured && <Badge className="bg-[#FF4D00]/20 text-[#FF4D00] text-xs">{t("featured")}</Badge>}
                    {product.is_dropshipping && product.shipping_days && <Badge variant="outline" className="text-blue-400 border-blue-400/30 text-xs flex items-center gap-1"><Truck size={10} weight="bold" /> {product.shipping_days}</Badge>}
                  </div>
                  <Link to={`/tienda/producto/${product.id}`} className="hover:text-[#FF4D00]"><h3 className="text-lg font-semibold font-['Cabinet_Grotesk']">{product.name}</h3></Link>
                  <p className="text-sm text-gray-400 line-clamp-2 sm:line-clamp-3 whitespace-pre-wrap">{product.description}</p>
                  <div className="flex items-center justify-between mt-auto pt-2 flex-wrap gap-2">
                    <div>
                      <span className="text-xl font-bold text-[#FF4D00]">{formatPrice(product.price)}</span>
                      <p className="text-xs text-gray-500">{product.stock > 0 ? (lang === "en" ? "Available" : "Disponible") : t("no_stock")}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/tienda/producto/${product.id}`}><Button size="sm" variant="outline" className="border-white/10 text-gray-300">{lang === "en" ? "Details" : "Ver detalle"}</Button></Link>
                      <Button size="sm" variant="outline" className="border-white/10 text-gray-300 hover:text-white" onClick={() => addToCart(product)} disabled={product.stock <= 0} data-testid={`add-to-cart-${product.id}`}>
                        {product.stock <= 0 ? t("sold_out") : t("add_to_cart")}
                      </Button>
                      {product.stock > 0 && <Button size="sm" className="bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" data-testid={`quick-buy-${product.id}`} onClick={() => { setQuickBuyProduct(product); setCart([{ ...product, quantity: 1 }]); setCheckoutOpen(true); }}>{lang === "en" ? "Buy" : "Comprar"}</Button>}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          {filtered.length === 0 && <div className="text-center py-20 text-gray-400"><ShoppingBag size={48} className="mx-auto mb-4 opacity-30" /><p>{t("no_products_category")}</p></div>}
        </div>
      </section>

      {/* Cart Dialog */}
      <Dialog open={cartOpen} onOpenChange={setCartOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md" data-testid="cart-dialog">
          <DialogHeader><DialogTitle className="font-['Cabinet_Grotesk'] text-xl">{t("cart")}</DialogTitle><DialogDescription>{t("cart_review")}</DialogDescription></DialogHeader>
          {cart.length === 0 ? <p className="text-center text-gray-400 py-8">{t("cart_empty")}</p> : (
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {cart.map(item => (
                <div key={`${item.id}::${item.selected_size || ""}::${item.selected_color || ""}`} className="flex items-center gap-3 p-3 bg-[#1A1A1A] border border-white/5">
                  <img src={item.image_url} alt={item.name} className="w-14 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    {(item.selected_size || item.selected_color) && (
                      <p className="text-[10px] text-gray-500 truncate">
                        {[item.selected_color, item.selected_size && `Talla ${item.selected_size}`].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-xs text-[#FF4D00] font-bold">{formatPrice(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(item.id, -1)} className="text-gray-400 hover:text-white"><Minus size={14} /></button>
                    <span className="text-sm w-6 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, 1)} className="text-gray-400 hover:text-white"><Plus size={14} /></button>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="text-gray-500 hover:text-red-400"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
          {cart.length > 0 && (
            <div className="pt-4 border-t border-white/10">
              <div className="flex justify-between items-center mb-4"><span className="text-sm text-gray-400">{t("total")}</span><span className="text-xl font-bold text-[#FF4D00]">{formatPrice(cartTotal)}</span></div>
              <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold" onClick={() => { setCartOpen(false); setCheckoutOpen(true); }} data-testid="cart-checkout-btn">{t("proceed_payment")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-['Cabinet_Grotesk'] text-xl">{t("checkout_title")}</DialogTitle><DialogDescription>{t("checkout_desc")}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-gray-400 text-xs">{t("full_name")}</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={customerInfo.name} onChange={e => setCustomerInfo(p => ({ ...p, name: e.target.value }))} data-testid="checkout-name-input" /></div>
            <div><Label className="text-gray-400 text-xs">{t("email")}</Label><Input type="email" className="bg-[#1A1A1A] border-white/10 mt-1" value={customerInfo.email} onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))} data-testid="checkout-email-input" /></div>
            <div><Label className="text-gray-400 text-xs">{t("phone")}</Label><Input className="bg-[#1A1A1A] border-white/10 mt-1" value={customerInfo.phone} onChange={e => setCustomerInfo(p => ({ ...p, phone: e.target.value }))} data-testid="checkout-phone-input" /></div>

            {/* Delivery Options */}
            <div className="pt-3 border-t border-white/10">
              <Label className="text-sm font-semibold text-white mb-3 block">{lang === "en" ? "Delivery Method" : "Metodo de Entrega"}</Label>
              <div className="space-y-2">
                {[
                  { value: "event_pickup", label: lang === "en" ? "Pick up at an event" : "Retirar en un evento" },
                  { value: "shipping", label: lang === "en" ? "Ship to my address" : "Enviar a mi direccion" },
                  { value: "store_pickup", label: lang === "en" ? "Pick up at store/point" : "Retirar en punto de retiro" },
                  { value: "coordinate", label: lang === "en" ? "Coordinate pickup with seller (local / special)" : "Retiro a coordinar con el vendedor (entregas locales / envios especiales)" },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${delivery.method === opt.value ? "bg-[#FF4D00]/10 border-[#FF4D00]/30" : "bg-[#1A1A1A] border-white/5"}`}>
                    <input type="radio" name="delivery" value={opt.value} checked={delivery.method === opt.value}
                      onChange={() => setDelivery(p => ({ ...p, method: opt.value }))} className="accent-[#FF4D00]" />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Event pickup selector */}
              {delivery.method === "event_pickup" && (
                <div className="mt-3">
                  <Label className="text-xs text-gray-400">{lang === "en" ? "Select Event" : "Selecciona el Evento"}</Label>
                  <Select value={delivery.event_id} onValueChange={v => {
                    const ev = events.find(e => e.id === v);
                    setDelivery(p => ({ ...p, event_id: v, event_name: ev?.title || "" }));
                  }}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 mt-1" data-testid="checkout-event-select"><SelectValue placeholder={lang === "en" ? "Choose event..." : "Elegir evento..."} /></SelectTrigger>
                    <SelectContent>
                      {events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.title} - {ev.date}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Shipping address */}
              {delivery.method === "shipping" && (
                <div className="mt-3 space-y-3">
                  <div><Label className="text-xs text-gray-400">{lang === "en" ? "Address" : "Direccion"} *</Label>
                    <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.address} onChange={e => setDelivery(p => ({ ...p, address: e.target.value }))} data-testid="checkout-address" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-400">{lang === "en" ? "City" : "Ciudad"} *</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.city} onChange={e => setDelivery(p => ({ ...p, city: e.target.value }))} data-testid="checkout-city" /></div>
                    <div><Label className="text-xs text-gray-400">{lang === "en" ? "Region" : "Region"}</Label>
                      <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.region} onChange={e => setDelivery(p => ({ ...p, region: e.target.value }))} /></div>
                  </div>
                  <div><Label className="text-xs text-gray-400">{lang === "en" ? "Notes" : "Notas de envio"}</Label>
                    <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.notes} onChange={e => setDelivery(p => ({ ...p, notes: e.target.value }))} placeholder={lang === "en" ? "Apt, references..." : "Depto, referencias..."} /></div>
                </div>
              )}

              {/* Store pickup info */}
              {delivery.method === "store_pickup" && (
                <div className="mt-3 bg-[#1A1A1A] border border-white/5 p-3">
                  <p className="text-sm text-gray-300">{lang === "en" ? "Pick up at:" : "Retira en:"} Coyhaique, Aysen</p>
                  <p className="text-xs text-gray-500 mt-1">{lang === "en" ? "We'll notify you when ready" : "Te notificaremos cuando este listo"}</p>
                </div>
              )}

              {/* Coordinate pickup with seller (local / special delivery) */}
              {delivery.method === "coordinate" && (
                <div className="mt-3 space-y-3">
                  <div className="bg-[#1A1A1A] border border-[#FF4D00]/20 p-3">
                    <p className="text-sm text-gray-200 font-semibold">{lang === "en" ? "Coordinate with seller" : "Coordinaras con el vendedor"}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{lang === "en" ? "Indicate your preferred pickup or delivery location/time. We'll confirm via WhatsApp." : "Indica tu preferencia de retiro o entrega (lugar / horario). Confirmamos por WhatsApp."}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">{lang === "en" ? "Preferred location / area" : "Lugar o sector preferido"}</Label>
                    <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.address} onChange={e => setDelivery(p => ({ ...p, address: e.target.value }))} placeholder={lang === "en" ? "Ex: Coyhaique downtown, Pza. de Armas..." : "Ej: Centro de Coyhaique, Plaza de Armas..."} data-testid="checkout-coord-place" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">{lang === "en" ? "Preferred date/time" : "Dia y hora preferida"}</Label>
                    <Input className="bg-[#1A1A1A] border-white/10 mt-1" value={delivery.notes} onChange={e => setDelivery(p => ({ ...p, notes: e.target.value }))} placeholder={lang === "en" ? "Ex: Sat afternoon, or anytime weekdays..." : "Ej: Sabado tarde, o cualquier dia de semana..."} data-testid="checkout-coord-time" />
                  </div>
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-white/10">
              {/* Discount Code */}
              <div className="mb-3">
                <Label className="text-xs text-gray-400">{lang === "en" ? "Discount Code" : "Codigo de Descuento"}</Label>
                <div className="flex gap-2 mt-1">
                  <Input className="bg-[#1A1A1A] border-white/10 flex-1 uppercase" value={discountCode} onChange={e => { setDiscountCode(e.target.value); setDiscountInfo(null); }} placeholder="CODIGO" data-testid="checkout-discount-input" />
                  <Button variant="outline" size="sm" className="border-white/10 text-gray-300 h-10" onClick={validateDiscount} disabled={discountLoading} data-testid="checkout-discount-btn">
                    {discountLoading ? "..." : (lang === "en" ? "Apply" : "Aplicar")}
                  </Button>
                </div>
                {discountInfo && <p className="text-xs text-green-400 mt-1">{discountInfo.description} (-{formatPrice(discountInfo.discount_amount)})</p>}
              </div>
              <div className="flex justify-between text-sm mb-1"><span className="text-gray-400">{cart.length} {t("products_label")}</span><span className={discountInfo ? "line-through text-gray-500" : "font-bold text-[#FF4D00]"}>{formatPrice(cartTotal)}</span></div>
              {discountInfo && <div className="flex justify-between text-sm mb-3"><span className="text-green-400">Total con descuento</span><span className="font-bold text-[#FF4D00] text-lg">{formatPrice(finalTotal)}</span></div>}
              <Button className="w-full bg-[#FF4D00] hover:bg-[#E64500] text-white font-bold h-12" onClick={handleCheckout} disabled={loading} data-testid="checkout-confirm-btn">{loading ? t("processing") : t("confirm_order")}</Button>
              <p className="text-xs text-gray-500 text-center mt-3">{t("secure_payment")}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Dialog removed - now using dedicated /tienda/producto/:id page */}
    </main>
  );
}
